import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL } from '../../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
  remove,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import mapSortParam from './iksoris-parking-ticket-cashless-machine-sort.mapper'
import filterMapper from './iksoris-parking-ticket-cashless-machine-filter.mapper'
import { CacheableDataProviderExtensionResult } from '../../../common/data-provider'
import { SetAPIKeyDto } from '../../../dto/device/ticket-machines/set-api-key.dto'
import { RemoveAPIKeyDto } from '../../../dto/device/ticket-machines/remove-api-key.dto'
import { IksorisParkingTicketCashlessMachineDto } from '../../../dto/device/ticket-machines/iksoris-parking-ticket-cashless-machine/iksoris-parking-ticket-cashless-machine.dto'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<IksorisParkingTicketCashlessMachineDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<IksorisParkingTicketCashlessMachineDto[]>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<IksorisParkingTicketCashlessMachineDto>> => {
    const data = await get<IksorisParkingTicketCashlessMachineDto>(
      IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<IksorisParkingTicketCashlessMachineDto>> => {
    const data = await get<IksorisParkingTicketCashlessMachineDto[]>(
      IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateIksorisParkingTicketCashlessMachineRequest>,
  ): Promise<CreateResult<IksorisParkingTicketCashlessMachineDto>> => {
    const tscan = await post<
      CreateIksorisParkingTicketCashlessMachineRequest[],
      IksorisParkingTicketCashlessMachineDto[]
    >(IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL, [data])
    return {
      data: tscan[0],
    }
  },
  update: async (
    resource: string,
    {
      id,
      data,
      previousData,
    }: UpdateParams<IksorisParkingTicketCashlessMachineDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      IksorisParkingTicketCashlessMachineDto
    >(IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL, {
      id,
      name: data.name !== previousData.name ? data.name : undefined,
      isWatched:
        data.isWatched !== previousData.isWatched ? data.isWatched : undefined,
      categoryId:
        data.categoryId !== previousData.categoryId
          ? data.categoryId
          : undefined,
      cameraId:
        data.cameraId !== previousData.cameraId ? data.cameraId : undefined,
      address: data.address !== previousData.address ? data.address : undefined,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<IksorisParkingTicketCashlessMachineDto>(
      IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({ data: deleted })
  },
  setAPIKey: async (
    resource: string,
    params: SetAPIKeyDto,
  ): Promise<CacheableDataProviderExtensionResult<SetAPIKeyDto>> => {
    const response = await put<SetAPIKeyDto, SetAPIKeyDto>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/SetAPIKey`,
      params,
    )
    return {
      data: response,
    }
  },
  removeAPIKey: async (
    resource: string,
    params: RemoveAPIKeyDto,
  ): Promise<CacheableDataProviderExtensionResult<RemoveAPIKeyDto>> => {
    const response = await put<RemoveAPIKeyDto, RemoveAPIKeyDto>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/RemoveAPIKey`,
      params,
    )
    return {
      data: response,
    }
  },
  lock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const locked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/Lock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: locked })
  },
  unlock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const unlocked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/Unlock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: unlocked })
  },
  restart: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const unlocked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/Restart/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: unlocked })
  },
  rebootDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const rebooted = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/RebootDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: rebooted })
  },
  shutDownDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const shutDown = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASHLESS_MACHINES_URL}/ShutDownDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: shutDown })
  },
} as IksorisParkingTicketCashMachinesProvider

interface IksorisParkingTicketCashMachinesProvider extends DataProvider {
  setAPIKey: (
    resource: string,
    params: SetAPIKeyDto,
  ) => Promise<CacheableDataProviderExtensionResult<SetAPIKeyDto>>
  removeAPIKey: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  restart: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  lock: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  unlock: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  rebootDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  shutDownDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
}

export interface CreateIksorisParkingTicketCashlessMachineRequest {
  readonly name: string
  readonly isWatched: string
  readonly categoryId: number
  readonly cameraId: number
  readonly address: string
}

export interface TicketMachineIdParams {
  readonly ticketMachineId: number
}

export interface TicketMachineIdRequest {
  readonly ticketMachineId: Identifier
}

export default provider
